import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { 
  Users, Briefcase, Send, TrendingUp, LayoutDashboard, UserCircle,
  Building2, Search, Plus, Eye, Edit, ChevronDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { UserProvider } from '../contexts/UserContext';
import CandidateFormComprehensive from '../components/CandidateFormComprehensive';
import ContactsManagement from '../components/ContactsManagement';
import CustomerManagement from './CustomerManagement';
import VendorManagement from './VendorManagement';
import Submissions from './Submissions';
import AISourcingTab from './AISourcingTab';
import StatCard from '../components/recruiting/StatCard';
import OverviewTab from '../components/recruiting/OverviewTab';
import IndustryJobDistribution from '../components/recruiting/IndustryJobDistribution';
import CandidatesTab from '../components/recruiting/CandidatesTab';
import JobsTab from '../components/recruiting/JobsTab';
import JobsTableView from '../components/recruiting/JobsTableView';
import TeamTab from '../components/recruiting/TeamTab';
import SubmissionsTab from '../components/recruiting/SubmissionsTab';
import JobDetailsSubmitModal from '../components/JobDetailsSubmitModal';
import LoadingOverlay, { LoadingBar } from '../components/LoadingOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UnifiedRecruiterDashboard = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Read tab from URL query parameter, default to 'overview'
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilteringData, setIsFilteringData] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Filter states for Jobs tab (can be set from Overview)
  const [jobFilters, setJobFilters] = useState({
    industry: 'all',
    priority: 'all',
    maxAge: 60
  });
  
  // Data states
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  
  // Modal states
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Function to navigate to Jobs tab with filters
  const viewJobsWithFilter = (industry, priority) => {
    setJobFilters({
      industry: industry,
      priority: priority,
      maxAge: 60
    });
    setActiveTab('jobs');
    toast.success(`Viewing ${priority} priority jobs in ${industry}`);
  };

  // Fetch all recruiting users on mount
  useEffect(() => {
    fetchRecruitingUsers();
  }, []);
  
  // Handle navigation from Job modal to Companies tab
  useEffect(() => {
    window.onNavigateToCompaniesFromJob = () => {
      setActiveTab('companies');
    };
    
    return () => {
      delete window.onNavigateToCompaniesFromJob;
    };
  }, []);

  // Sync activeTab with URL query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const fetchRecruitingUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        const recruitingUsers = (data.users || []).filter(u => {
          // Check both single role and roles array for recruiting roles
          const userRoles = u.roles || [u.role];
          return userRoles.some(role => 
            role === 'recruiter' || 
            role === 'lead_recruiter' || 
            role === 'lead' || 
            role === 'recruiter_supervisor'
          );
        });
        setAvailableUsers(recruitingUsers);
        
        // Check if persona passed via navigation state (from JobView back button)
        const stateUser = location.state?.selectedUser;
        if (stateUser && recruitingUsers.some(u => u.id === stateUser.id)) {
          console.log('[Dashboard] Restoring persona from navigation state:', stateUser.full_name);
          setSelectedUser(stateUser);
        }
        // Auto-select first user only if no state and no current selection
        else if (recruitingUsers.length > 0 && !selectedUser && !stateUser) {
          setSelectedUser(recruitingUsers[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchCandidates = useCallback(async () => {
    try {
      console.log('┌─────────────────────────────────────────────────┐');
      console.log('│ [Dashboard] fetchCandidates() START             │');
      console.log('└─────────────────────────────────────────────────┘');
      
      // IMPORTANT: Fetch candidates for submissions display
      // We need candidates that are referenced in submissions, not just assigned to current user
      const userId = selectedUser?.id;
      
      if (!userId) {
        console.warn('[Dashboard] No user ID available for candidate fetch');
        setCandidates([]);
        return;
      }
      
      console.log('[Dashboard] Fetching for user:', selectedUser?.full_name, '(ID:', userId, ')');
      
      // Get candidates referenced in user's submissions first
      let submissionCandidateIds = [];
      try {
        console.log('[Dashboard] Step 1: Fetching submission candidate IDs...');
        const submissionsResponse = await fetch(`${BACKEND_URL}/api/submissions-list?user_id=${userId}&timeframe=all_time`);
        if (submissionsResponse.ok) {
          const submissionData = await submissionsResponse.json();
          submissionCandidateIds = submissionData.map(sub => sub.candidate_id).filter(Boolean);
          console.log('[Dashboard] - Found', submissionCandidateIds.length, 'candidate IDs from submissions');
        }
      } catch (error) {
        console.warn('[Dashboard] Could not fetch submission candidate IDs:', error);
      }
      
      // Primary query: user's assigned candidates + submission candidates
      const primaryUrl = `${BACKEND_URL}/api/candidates?primary_recruiter_id=${userId}&limit=5000`;
      
      console.log('[Dashboard] Step 2: Fetching primary candidates from:', primaryUrl);
      
      const response = await fetch(primaryUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch candidates: ${response.status}`);
      }
      
      const data = await response.json();
      let allCandidates = Array.isArray(data) ? data : [];
      console.log('[Dashboard] - Fetched', allCandidates.length, 'primary candidates');
      
      // If we have submission candidates not in the primary set, fetch them too
      if (submissionCandidateIds.length > 0) {
        const existingIds = new Set(allCandidates.map(c => c.id));
        const missingIds = submissionCandidateIds.filter(id => !existingIds.has(id));
        
        if (missingIds.length > 0) {
          console.log('[Dashboard] Step 3: Fetching', missingIds.length, 'additional candidates for submissions');
          try {
            const additionalResponse = await fetch(`${BACKEND_URL}/api/candidates/by-ids`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ candidate_ids: missingIds })
            });
            
            if (additionalResponse.ok) {
              const additionalCandidates = await additionalResponse.json();
              console.log('[Dashboard] - Fetched', additionalCandidates.length, 'additional candidates');
              allCandidates = [...allCandidates, ...additionalCandidates];
            }
          } catch (error) {
            console.warn('[Dashboard] Could not fetch additional candidates:', error);
          }
        } else {
          console.log('[Dashboard] Step 3: No additional candidates needed (all in primary set)');
        }
      }
      
      console.log('[Dashboard] Step 4: Setting candidates state');
      console.log('[Dashboard] - Total candidates:', allCandidates.length);
      console.log('[Dashboard] - Sample candidate IDs:', allCandidates.slice(0, 3).map(c => c.id));
      
      // Store candidates - CandidatesTab can load more if user clicks "All Candidates"
      setCandidates(allCandidates);
      
      console.log('┌─────────────────────────────────────────────────┐');
      console.log('│ [Dashboard] fetchCandidates() COMPLETE          │');
      console.log('└─────────────────────────────────────────────────┘');
      
    } catch (error) {
      console.error('[Dashboard] ERROR in fetchCandidates():', error);
      toast.error('Failed to load candidates');
    }
  }, [selectedUser?.id, selectedUser?.full_name]);

  const fetchJobs = useCallback(async () => {
    try {
      // Fetch with pagination - load 500 most recent jobs for better performance
      // Frontend can implement "Load More" if needed for older jobs
      const response = await fetch(`${BACKEND_URL}/api/jobs?limit=500&skip=0`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.status}`);
      }
      
      const data = await response.json();
      const allJobs = Array.isArray(data) ? data : [];
      
      console.log('[Dashboard] Fetched', allJobs.length, 'jobs (limited to 500 most recent)');
      setJobs(allJobs);
      
    } catch (error) {
      console.error('[Dashboard] Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    }
  }, []);

  const fetchSubmissions = useCallback(async (timeframe = 'all_time') => {
    try {
      console.log('┌─────────────────────────────────────────────────┐');
      console.log('│ [Dashboard] fetchSubmissions() START            │');
      console.log('└─────────────────────────────────────────────────┘');
      
      // CRITICAL: Always filter by user_id for proper data isolation
      if (!selectedUser?.id) {
        console.warn('[Dashboard] Cannot fetch submissions without user ID');
        return;
      }
      
      console.log('[Dashboard] Fetching for user:', selectedUser.full_name, '(ID:', selectedUser.id, ')');
      console.log('[Dashboard] Timeframe:', timeframe);
      
      // IMPORTANT: Fetch ALL submissions (all_time) so SubmissionsTab can do client-side filtering
      const fetchUrl = `${BACKEND_URL}/api/submissions-list?timeframe=all_time&user_id=${selectedUser.id}`;
      console.log('[Dashboard] Fetch URL:', fetchUrl);
      
      const response = await fetch(fetchUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch submissions: ${response.status}`);
      }
      
      const data = await response.json();
      const allSubmissions = Array.isArray(data) ? data : [];
      
      console.log('[Dashboard] Step 1: API returned', allSubmissions.length, 'submissions');
      
      // Debug: Show AI-sourced submissions
      const aiSourced = allSubmissions.filter(s => s.submission_source === 'ai_suggested');
      console.log('[Dashboard] Step 2: Found', aiSourced.length, 'AI-sourced submissions');
      if (aiSourced.length > 0) {
        console.log('[Dashboard] - Latest AI-sourced submission:', {
          id: aiSourced[0].id,
          candidate_name: aiSourced[0].candidate_name,
          job_title: aiSourced[0].job_title,
          date_added: aiSourced[0].date_added,
          submission_date: aiSourced[0].submission_date,
          created_at: aiSourced[0].created_at
        });
      }
      
      console.log('[Dashboard] Step 3: Setting submissions state');
      console.log('[Dashboard] - Previous count:', submissions.length);
      console.log('[Dashboard] - New count:', allSubmissions.length);
      
      setSubmissions(allSubmissions);
      
      console.log('┌─────────────────────────────────────────────────┐');
      console.log('│ [Dashboard] fetchSubmissions() COMPLETE         │');
      console.log('└─────────────────────────────────────────────────┘');
      
    } catch (error) {
      console.error('[Dashboard] ERROR in fetchSubmissions():', error);
      toast.error('Failed to load submissions');
    }
  }, [selectedUser]);

  const fetchTeamData = useCallback(async () => {
    if (!selectedUser) return;
    
    try {
      const teamsResponse = await fetch(`${BACKEND_URL}/api/teams`);
      if (!teamsResponse.ok) {
        console.warn('[Dashboard] Failed to fetch teams');
        return;
      }
      
      const teamsData = await teamsResponse.json();
      const allTeams = teamsData.teams || [];
      
      // Fetch all users upfront for team member/lead lookups
      const usersResponse = await fetch(`${BACKEND_URL}/api/users`);
      if (!usersResponse.ok) {
        console.warn('[Dashboard] Failed to fetch users');
        return;
      }
      
      const usersData = await usersResponse.json();
      const allUsers = usersData.users || [];
      
      // If user is a lead recruiter, get their team
      const userRoles = selectedUser.roles || [selectedUser.role];
      const isLeadRecruiter = userRoles.some(role => 
        role === 'lead_recruiter' || role === 'lead' || role === 'recruiter_supervisor'
      );
      const isRegularRecruiter = userRoles.includes('recruiter');
      
      if (isLeadRecruiter) {
        const userTeam = allTeams.find(t => t.team_lead_id === selectedUser.id);
        setMyTeam(userTeam);
        
        if (userTeam && userTeam.team_members) {
          // Get team member details
          const members = allUsers.filter(u => 
            userTeam.team_members.includes(u.id)
          );
          setTeamMembers(members);
          console.log(`[TEAM] ${selectedUser.full_name} (lead) manages team with ${members.length} members`);
        }
      }
      // If user is a regular recruiter, find their team and team lead
      else if (isRegularRecruiter) {
        console.log(`[TEAM] ${selectedUser.full_name} is a regular recruiter, looking for team...`);
        const recruiterTeam = allTeams.find(t => 
          t.team_members && t.team_members.includes(selectedUser.id)
        );
        
        if (recruiterTeam) {
          console.log(`[TEAM] Found team for ${selectedUser.full_name}:`, recruiterTeam.team_name);
          setMyTeam(recruiterTeam);
          
          // Immediately fetch team lead
          if (recruiterTeam.team_lead_id) {
            console.log(`[TEAM] Fetching team lead with ID: ${recruiterTeam.team_lead_id}`);
            const lead = allUsers.find(u => u.id === recruiterTeam.team_lead_id);
            
            if (lead) {
              console.log(`[TEAM] ✅ Found team lead:`, lead.full_name || lead.name, 'with assigned_clients:', lead.assigned_clients);
              setTeamMembers([lead]);
            } else {
              console.log(`[TEAM] ❌ Team lead not found for ID: ${recruiterTeam.team_lead_id}`);
              setTeamMembers([]);
            }
          }
        } else {
          console.log(`[TEAM] No team found for ${selectedUser.full_name}`);
          setMyTeam(null);
          setTeamMembers([]);
        }
      }
    } catch (error) {
      console.error('[Dashboard] Error fetching team data:', error);
    }
  }, [selectedUser]);

  // Fetch all user data when selected user changes
  // IMPORTANT: This useEffect must come AFTER all useCallback definitions to avoid initialization errors
  useEffect(() => {
    if (!selectedUser) return;
    
    const loadUserData = async () => {
      setLoading(true);
      
      // Clear previous user data
      setJobs([]);
      setCandidates([]);
      setSubmissions([]);
      setMyTeam(null);
      setTeamMembers([]);
      
      try {
        await Promise.all([
          fetchCandidates(),
          fetchJobs(),
          fetchSubmissions(),
          fetchTeamData()
        ]);
      } catch (error) {
        console.error('[Dashboard] Error fetching user data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, [selectedUser, fetchCandidates, fetchJobs, fetchSubmissions, fetchTeamData]);

  const getRoleBadge = (role) => {
    const badges = {
      'recruiter': { text: 'Recruiter', color: 'bg-green-100 text-green-800' },
      'lead': { text: 'Lead Recruiter', color: 'bg-blue-100 text-blue-800' },
      'lead_recruiter': { text: 'Lead Recruiter', color: 'bg-blue-100 text-blue-800' },
      'recruiter_supervisor': { text: 'Recruiter Supervisor', color: 'bg-purple-100 text-purple-800' }
    };
    return badges[role] || { text: role, color: 'bg-gray-100 text-gray-800' };
  };

  if (!selectedUser) {
    return (
      <>
        <LoadingBar isLoading={true} />
        <LoadingOverlay 
          isLoading={true} 
          type="fullscreen" 
          message="Loading Dashboard"
        />
      </>
    );
  }

  const badge = getRoleBadge(selectedUser.role);

  return (
    <UserProvider initialUser={selectedUser}>
      <div className="min-h-screen bg-gray-50">
        {/* Loading Bar for data fetching */}
        <LoadingBar isLoading={loading || isFilteringData} />
      {/* Header with User Proxy Selector */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Recruiting Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">
                {selectedUser.industries && selectedUser.industries.length > 0 && (
                  <span>Industries: {selectedUser.industries.join(', ')}</span>
                )}
              </p>
            </div>
            
            {/* User Proxy Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center gap-3 px-4 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <UserCircle className="h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{selectedUser.full_name}</p>
                    <p className={`text-xs px-2 py-0.5 rounded ${badge.color}`}>{badge.text}</p>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-600" />
              </button>
              
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 uppercase px-2">Switch User Context</p>
                  </div>
                  {availableUsers.map(user => {
                    const userBadge = getRoleBadge(user.role);
                    return (
                      <button
                        key={user.id}
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserDropdown(false);
                          toast.success(`Switched to ${user.full_name}`);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-0 transition-colors ${
                          selectedUser.id === user.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{user.full_name}</p>
                            <p className="text-xs text-gray-600">{user.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${userBadge.color}`}>
                            {userBadge.text}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-1 -mb-px">
            {[
              { key: 'overview', label: 'Overview', icon: LayoutDashboard },
              { key: 'companies', label: 'Companies', icon: Building2 },
              { key: 'contacts', label: 'Contacts', icon: UserCircle },
              { key: 'jobs', label: 'Jobs', icon: Briefcase },
              { key: 'candidates', label: 'Candidates', icon: Users },
              { key: 'submissions', label: 'Submissions', icon: Send },
              { key: 'ai-sourcing', label: 'AI Sourcing', icon: TrendingUp },
              ...(selectedUser.role === 'lead' || selectedUser.role === 'recruiter_supervisor' || selectedUser.role === 'lead_recruiter' || selectedUser.role === 'recruiter' ? [{ key: 'team', label: 'My Team', icon: Users }] : [])
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
        {/* Show loading overlay when initially loading data */}
        {loading && (
          <LoadingOverlay 
            isLoading={true} 
            type="inline" 
            message="Fetching data..."
          />
        )}
        
        {/* Show content when not loading */}
        {!loading && (
          <>
            {activeTab === 'overview' && <OverviewTab 
          selectedUser={selectedUser} 
          candidates={candidates} 
          jobs={jobs} 
          submissions={submissions} 
          myTeam={myTeam}
          availableUsers={availableUsers}
          teamLead={(() => {
            const userRoles = selectedUser.roles || [selectedUser.role];
            const isRegularRecruiter = userRoles.includes('recruiter');
            const teamLead = isRegularRecruiter && teamMembers.length > 0 ? teamMembers[0] : null;
            console.log(`[RENDER] Passing teamLead to OverviewTab:`, teamLead?.name || 'null', `(teamMembers.length: ${teamMembers.length})`);
            return teamLead;
          })()}
          onViewJobs={viewJobsWithFilter} 
        />}
        {activeTab === 'candidates' && <CandidatesTab selectedUser={selectedUser} candidates={candidates} submissions={submissions} myTeam={myTeam} teamLead={teamMembers.length > 0 ? teamMembers[0] : null} onRefresh={fetchCandidates} onAddCandidate={() => setShowAddCandidateModal(true)} />}
        {activeTab === 'jobs' && (
          <JobsTableView 
            selectedUser={selectedUser} 
            jobs={jobs}
            submissions={submissions}
            myTeam={myTeam}
            teamLead={(() => {
              const userRoles = selectedUser.roles || [selectedUser.role];
              const isRegularRecruiter = userRoles.includes('recruiter');
              return isRegularRecruiter && teamMembers.length > 0 ? teamMembers[0] : null;
            })()}
            initialFilters={jobFilters}
            onViewJob={(job) => {
              setSelectedJob(job);
              setShowJobDetailsModal(true);
            }}
            onJobCreated={() => {
              // Refresh jobs list when a new job is created
              fetchJobs();
            }}
            onRefreshSubmissions={() => {
              fetchSubmissions('all_time'); 
              fetchCandidates(); // Also refresh candidates to include newly referenced candidates
            }}
          />
        )}
        {activeTab === 'submissions' && <SubmissionsTab candidates={candidates} jobs={jobs} submissions={submissions} selectedUser={selectedUser} myTeam={myTeam} onRefreshSubmissions={() => { fetchSubmissions('all_time'); fetchCandidates(); }} />}
        {activeTab === 'ai-sourcing' && <AISourcingTab selectedUser={selectedUser} myTeam={myTeam} teamMembers={teamMembers} onRefresh={async () => { 
          console.log('═══════════════════════════════════════════════════');
          console.log('[Dashboard] onRefresh CALLBACK TRIGGERED FROM AI SOURCING');
          console.log('═══════════════════════════════════════════════════');
          console.log('[Dashboard] Current user:', selectedUser?.full_name);
          console.log('[Dashboard] Current submissions count:', submissions.length);
          console.log('[Dashboard] Current candidates count:', candidates.length);
          console.log('[Dashboard] Calling fetchSubmissions()...');
          const submissionsFetchStart = Date.now();
          await fetchSubmissions('all_time');
          console.log('[Dashboard] fetchSubmissions() complete (took', Date.now() - submissionsFetchStart, 'ms)');
          console.log('[Dashboard] Calling fetchCandidates()...');
          const candidatesFetchStart = Date.now();
          await fetchCandidates();
          console.log('[Dashboard] fetchCandidates() complete (took', Date.now() - candidatesFetchStart, 'ms)');
          console.log('═══════════════════════════════════════════════════');
          console.log('[Dashboard] onRefresh CALLBACK COMPLETE');
          console.log('═══════════════════════════════════════════════════');
        }} />}
        {activeTab === 'contacts' && <ContactsManagement selectedUser={selectedUser} myTeam={myTeam} teamMembers={teamMembers} />}
        {activeTab === 'companies' && <CustomerManagement readOnly={false} selectedUser={selectedUser} myTeam={myTeam} teamMembers={teamMembers} />}
            {activeTab === 'team' && (selectedUser.role === 'lead' || selectedUser.role === 'recruiter_supervisor' || selectedUser.role === 'lead_recruiter' || selectedUser.role === 'recruiter') && (
              <TeamTab selectedUser={selectedUser} myTeam={myTeam} teamMembers={teamMembers} onRefresh={fetchTeamData} />
            )}
          </>
        )}
      </div>

      {/* Add Candidate Modal */}
      {showAddCandidateModal && (
        <CandidateFormComprehensive
          candidate={null}
          currentUser={selectedUser}
          onClose={() => setShowAddCandidateModal(false)}
          onSuccess={() => {
            setShowAddCandidateModal(false);
            fetchCandidates(); // Refresh the candidate list
            // Toast message already shown by CandidateFormComprehensive
          }}
        />
      )}

      {/* Job Details and Submit Modal */}
      <JobDetailsSubmitModal
        isOpen={showJobDetailsModal}
        job={selectedJob}
        onClose={() => {
          setShowJobDetailsModal(false);
          setSelectedJob(null);
        }}
        onSubmitSuccess={() => {
          fetchSubmissions();
          toast.success('Candidates submitted successfully!');
        }}
      />
      </div>
    </UserProvider>
  );
};

export default UnifiedRecruiterDashboard;
